import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Snackbar,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { mergeOpsApiRef } from '../../api/MergeOpsApi';
import { actionErrorMessage } from '../../hooks/usePrActions';
import { releaseBranch, workflowRunsUrl } from '../../links';

const PROMOTE_WORKFLOW = 'command--mergeops-promote.yaml';
const CHERRYPICK_WORKFLOW = 'command--mergeops-cherrypick.yaml';
const DEPLOYPR_HOTFIX_WORKFLOW =
  'command--mergeops-deploypr-hotfix-canary.yaml';

/** Success Alert shown after a workflow_dispatch, linking to the run. */
const DispatchedAlert = ({
  label,
  workflowFile,
  onClose,
}: {
  label: string;
  workflowFile: string;
  onClose: () => void;
}) => (
  <Alert severity="success" onClose={onClose}>
    {label} dispatched — a PR will open once the run finishes (~1 min).{' '}
    <Link
      href={workflowRunsUrl(workflowFile)}
      target="_blank"
      rel="noopener noreferrer"
      color="inherit"
      style={{ textDecoration: 'underline', fontWeight: 600 }}
    >
      View run ↗
    </Link>{' '}
    then Refresh.
  </Alert>
);

/**
 * Promote → prd button. Promote only targets prd (stg → prd), so it needs no
 * input beyond a confirm step. Shared by the pipeline cards.
 */
export const PromoteButton = ({
  componentName,
  currentTag,
  onDispatched,
  canOperate = true,
}: {
  componentName: string;
  currentTag?: string | null;
  /** Called after a successful dispatch so the parent can poll for the new PR. */
  onDispatched?: () => void;
  /** When false the operator isn't a member of the owning team; disable. */
  canOperate?: boolean;
}) => {
  const api = useApi(mergeOpsApiRef);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // The backend picks the legacy or the deploypr workflow by the project's
  // deploy system; link the success toast to whichever it dispatched.
  const [workflowFile, setWorkflowFile] = useState(PROMOTE_WORKFLOW);

  const run = async () => {
    setConfirm(false);
    setBusy(true);
    setError(null);
    try {
      const res = await api.triggerPromote(componentName);
      setWorkflowFile(res.workflow ?? PROMOTE_WORKFLOW);
      setDone(true);
      onDispatched?.();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title="Promote the current stg tag to prd (opens a prd deploy PR)">
        <span>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            disabled={busy || !canOperate}
            onClick={() => setConfirm(true)}
          >
            {busy ? 'Dispatching…' : '🚀 Promote'}
          </Button>
        </span>
      </Tooltip>

      <Dialog open={confirm} onClose={() => !busy && setConfirm(false)}>
        <DialogTitle>Promote to prd?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Dispatches the MergeOps promote workflow on{' '}
            <code>knowledge-work/knowledgework</code>. It opens a prd deploy PR
            pinned to <strong>{componentName}</strong>'s current stg tag
            {currentTag ? (
              <>
                {' '}
                (prd is on <code>{currentTag}</code> now)
              </>
            ) : null}
            . It does not deploy directly — you still merge the PR.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={run}>
            Promote
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={done}
        autoHideDuration={15000}
        onClose={() => setDone(false)}
      >
        <DispatchedAlert
          label="Promote"
          workflowFile={workflowFile}
          onClose={() => setDone(false)}
        />
      </Snackbar>
      <Snackbar open={!!error} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error && actionErrorMessage(error)}
        </Alert>
      </Snackbar>
    </>
  );
};

/**
 * Pick (cherry-pick) button. Cherry-pick takes a merged source PR number and a
 * target branch, so it opens a small dialog to collect them. Shared by the
 * pipeline cards.
 */
export const CherryPickButton = ({
  componentName,
  projectName,
  tag,
  onDispatched,
  canOperate = true,
}: {
  componentName: string;
  projectName: string;
  /** Current deployed tag of this card's env; pre-fills the target release branch. */
  tag?: string | null;
  /** Called after a successful dispatch so the parent can poll for the new PR. */
  onDispatched?: () => void;
  /** When false the operator isn't a member of the owning team; disable. */
  canOperate?: boolean;
}) => {
  const api = useApi(mergeOpsApiRef);
  const defaultTo = tag ? releaseBranch(projectName, tag) : '';
  const [open, setOpen] = useState(false);
  const [prNumber, setPrNumber] = useState('');
  const [to, setTo] = useState(defaultTo);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const prValue = Number(prNumber);
  const valid =
    Number.isInteger(prValue) && prValue > 0 && to.trim().length > 0;

  // Seed the target with this card's release branch each time the dialog opens
  // (the tag may have loaded after the initial render).
  const openDialog = () => {
    setTo(defaultTo);
    setOpen(true);
  };

  const close = () => {
    if (!busy) setOpen(false);
  };

  const run = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await api.triggerCherryPick(componentName, prValue, to.trim());
      setDone(true);
      setOpen(false);
      setPrNumber('');
      setTo('');
      onDispatched?.();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title="Hotfix via cherry-pick: open a 🍒 PR of a merged PR into this env's release branch">
        <Button
          size="small"
          variant="outlined"
          disabled={!canOperate}
          onClick={openDialog}
        >
          🍒 Hotfix
        </Button>
      </Tooltip>

      <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle>Hotfix (cherry-pick) a merged PR</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Hotfix via cherry-pick: dispatches the MergeOps cherry-pick workflow
            on <code>knowledge-work/knowledgework</code>. It opens a 🍒 PR of
            the source PR into the target release branch — it does not deploy
            directly.
          </DialogContentText>
          <TextField
            margin="dense"
            label="Source PR number"
            placeholder="e.g. 121053"
            fullWidth
            value={prNumber}
            onChange={e => setPrNumber(e.target.value)}
            disabled={busy}
          />
          <TextField
            margin="dense"
            label="Target branch (to)"
            placeholder="e.g. release/auth-frontend/20260521-1739"
            fullWidth
            value={to}
            onChange={e => setTo(e.target.value)}
            disabled={busy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={run}
            disabled={!valid || busy}
          >
            {busy ? 'Dispatching…' : 'Run cherry-pick'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={done}
        autoHideDuration={15000}
        onClose={() => setDone(false)}
      >
        <DispatchedAlert
          label="Cherry-pick"
          workflowFile={CHERRYPICK_WORKFLOW}
          onClose={() => setDone(false)}
        />
      </Snackbar>
      <Snackbar open={!!error} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error && actionErrorMessage(error)}
        </Alert>
      </Snackbar>
    </>
  );
};

/**
 * Hotfix button for deploypr-managed (Go mergeops) components. Dispatches the
 * deploypr hotfix workflow, which resolves the release branch holding this
 * stage's running version and opens a hotfix PR onto it — the fix is pushed
 * onto that PR's branch, then merged. Legacy components hotfix via
 * CherryPickButton instead.
 */
export const HotfixButton = ({
  componentName,
  env,
  currentTag,
  onDispatched,
  canOperate = true,
}: {
  componentName: string;
  /** The stage to hotfix (this card's env). */
  env: 'stg' | 'prd';
  /** Current deployed tag of this card's env, shown in the confirm dialog. */
  currentTag?: string | null;
  /** Called after a successful dispatch so the parent can poll for the new PR. */
  onDispatched?: () => void;
  /** When false the operator isn't a member of the owning team; disable. */
  canOperate?: boolean;
}) => {
  const api = useApi(mergeOpsApiRef);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [workflowFile, setWorkflowFile] = useState(DEPLOYPR_HOTFIX_WORKFLOW);

  const run = async () => {
    setConfirm(false);
    setBusy(true);
    setError(null);
    try {
      const res = await api.triggerHotfix(componentName, env);
      setWorkflowFile(res.workflow ?? DEPLOYPR_HOTFIX_WORKFLOW);
      setDone(true);
      onDispatched?.();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip
        title={`Hotfix ${env}: open a hotfix PR on the release branch of the running version`}
      >
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={busy || !canOperate}
            onClick={() => setConfirm(true)}
          >
            {busy ? 'Dispatching…' : '🩹 Hotfix'}
          </Button>
        </span>
      </Tooltip>

      <Dialog open={confirm} onClose={() => !busy && setConfirm(false)}>
        <DialogTitle>Hotfix {env}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Dispatches the MergeOps deploypr hotfix workflow on{' '}
            <code>knowledge-work/knowledgework</code>. It resolves the release
            branch <strong>{componentName}</strong>'s current {env} version
            {currentTag ? (
              <>
                {' '}
                (<code>{currentTag}</code>)
              </>
            ) : null}{' '}
            lives on and opens a hotfix PR onto it. Push the fix onto the PR's
            branch, then merge — it does not deploy directly.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={run}>
            Hotfix
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={done}
        autoHideDuration={15000}
        onClose={() => setDone(false)}
      >
        <DispatchedAlert
          label="Hotfix"
          workflowFile={workflowFile}
          onClose={() => setDone(false)}
        />
      </Snackbar>
      <Snackbar open={!!error} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error && actionErrorMessage(error)}
        </Alert>
      </Snackbar>
    </>
  );
};
